/* eslint-disable */
/* tslint:disable */

/**
 * TCS does not have swagger docs. As a result, this api is NOT autogenerated by swagger-typescript-api.
 * https://atlassian.slack.com/archives/C03S3BHQQBD/p1675980355940209?thread_ts=1675959995.743339&cid=C03S3BHQQBD
 */

import type { TcsSidecarApi as TcsSidecarApiContract } from '@post-office/message-template-contracts/clients/tcs';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { AxiosClientDecorationOptions, AxiosClientFactoryConfig } from './types';

import { HttpClient, type RequestParams } from './http-client';
import type { CustomEmailFooter, RovoOnboardingStateOrgLevel } from './data-contracts';

export interface CryptorApiClient {
	cryptor: {
		encryptString: (args: {
			plainText: string;
			keyAlias: string;
			encryptionContext?: Map<string, string>;
		}) => Promise<AxiosResponse<{ cipherText: string }>>;

		decryptString: (args: {
			cipherText: string;
			encryptionContext?: Map<string, string>;
		}) => Promise<
			AxiosResponse<{
				plainText: string;
			}>
		>;
	};
}

export interface TcsSidecarClient {
	organization: {
		getTcsRecord: <T>(key: string, params: RequestParams) => Promise<AxiosResponse<T>>;
	};
	cloud: {
		getTcsRecord: <T>(key: string, params: RequestParams) => Promise<AxiosResponse<T>>;
	};
	routing: {
		getTcsRecord: <T>(key: string, params?: RequestParams) => Promise<AxiosResponse<T>>;
	};
	resourceLink: {
		getCcLinks: <T>(resourceAri: string, params: RequestParams) => Promise<AxiosResponse<T>>;
	};
	ccDirectory: {
		getCcDirectory: <T>(resourceAri: string, params: RequestParams) => Promise<AxiosResponse<T>>;
	};
	settingsService: {
		getCustomEmailFooter: (
			orgId: string,
			appName: string,
			params: RequestParams,
		) => Promise<AxiosResponse<CustomEmailFooter>>;
		getRovoOnboardingStateOrgLevel: (
			orgId: string,
			params?: RequestParams,
		) => Promise<AxiosResponse<RovoOnboardingStateOrgLevel>>;
	};
}

export class TcsSidecarApi implements CryptorApiClient, TcsSidecarClient, TcsSidecarApiContract {
	tcsHttp: HttpClient;
	cryptorHttp: HttpClient;

	constructor(tcsHttp: HttpClient, cryptorHttp: HttpClient) {
		this.tcsHttp = tcsHttp;
		this.cryptorHttp = cryptorHttp;
	}

	async healthCheck(): Promise<void> {
		await this.tcsHttp.request<void, void>({
			path: '/healthcheck',
			method: 'GET',
			timeout: 3000,
		});
	}

	organization = {
		getTcsRecord: <T>(
			key: string,
			params: RequestParams = {},
		): Promise<AxiosResponse<T, any, {}>> =>
			this.tcsHttp.request<T, void>({
				path: `/entity/organization/${key}`,
				method: 'GET',
				...params,
			}),
	};

	cloud = {
		getTcsRecord: <T>(
			key: string,
			params: RequestParams = {},
		): Promise<AxiosResponse<T, any, {}>> =>
			this.tcsHttp.request<T, void>({
				path: `/entity/cloud/${key}`,
				method: 'GET',
				...params,
			}),
	};

	routing = {
		getTcsRecord: <T>(
			key: string,
			params: RequestParams = {},
		): Promise<AxiosResponse<T, any, {}>> =>
			this.tcsHttp.request<T, void>({
				path: `/entity/routing/${key}`,
				method: 'GET',
				...params,
			}),
	};

	resourceLink = {
		getCcLinks: <T>(
			resourceAri: string,
			params: RequestParams = {},
		): Promise<AxiosResponse<T, any, {}>> =>
			this.tcsHttp.request<T, void>({
				path: `/entity/CC_RESOURCE_LINK/${resourceAri}.cc-links`,
				method: 'GET',
				...params,
			}),
	};

	ccDirectory = {
		getCcDirectory: <T>(
			resourceAri: string,
			params: RequestParams = {},
		): Promise<AxiosResponse<T, any, {}>> =>
			this.tcsHttp.request<T, void>({
				path: `/entity/CC_RESOURCE_DIRECTORY/${resourceAri}.cc-directory`,
				method: 'GET',
				...params,
			}),
	};

	settingsService = {
		getCustomEmailFooter: (
			orgId: string,
			appName: string,
			params: RequestParams = {},
		): Promise<AxiosResponse<CustomEmailFooter, any, {}>> =>
			this.tcsHttp.request<CustomEmailFooter, void>({
				// Settings service expects the tcs key to be uri encoded, including the '-', since encodeURIComponent does not encode '-', manual replacement is needed
				path:
					'/entity/settings_service/' +
					encodeURIComponent(`ari:cloud:platform::custom-email-footer/${orgId}/${appName}`).replace(
						/-/g,
						'%2D',
					) +
					'/email-notification-footer',
				method: 'GET',
				...params,
			}),
		getRovoOnboardingStateOrgLevel: (
			orgId: string,
			params: RequestParams = {},
		): Promise<AxiosResponse<RovoOnboardingStateOrgLevel, any, {}>> =>
			this.tcsHttp.request<RovoOnboardingStateOrgLevel, void>({
				path:
					'/entity/settings_service/' +
					encodeURIComponent(
						`ari:cloud:platform::rovo-onboarding-state-org-level/${orgId}`,
					).replace(/-/g, '%2D'),
				method: 'GET',
				...params,
			}),
	};

	cryptor = {
		encryptString: (args: {
			plainText: string;
			keyAlias: string;
			encryptionContext?: Map<string, string>;
		}): Promise<AxiosResponse<{ cipherText: string }>> =>
			this.cryptorHttp.request({
				path: `/cryptor/encrypt/${args.keyAlias}`,
				method: 'POST',
				body: {
					plainText: args.plainText,
					encryptionContext: args.encryptionContext,
				},
			}),
		decryptString: (args: {
			cipherText: string;
			encryptionContext?: Map<string, string>;
		}): Promise<
			AxiosResponse<{
				plainText: string;
			}>
		> =>
			this.cryptorHttp.request({
				path: `/cryptor/decrypt`,
				method: 'POST',
				body: {
					cipherText: args.cipherText,
					encryptionContext: args.encryptionContext,
				},
			}),
	};
}

const DEFAULT_CRYPTOR_CLIENT_ID = 'post-office';

export type TcsSidecarApiOptions = {
	cryptorClientId?: string;
};

export async function initialiseTcsSidecarApi(
	axiosClient: (
		opts: AxiosClientDecorationOptions,
		config?: AxiosClientFactoryConfig,
	) => Promise<AxiosInstance>,
	opts?: AxiosClientDecorationOptions,
	config?: AxiosClientFactoryConfig,
	tcsSidecarApiOptions?: TcsSidecarApiOptions,
): Promise<TcsSidecarApi> {
	const tcsBaseUrl = process.env['TCS_SIDECAR_HTTP_URL'];
	const tcsAxiosInstance = await axiosClient(
		{ name: 'tcs-sidecar', ...opts },
		{ baseURL: tcsBaseUrl, ...config },
	);
	const tcsHttpClient = new HttpClient({
		axiosInstance: tcsAxiosInstance,
		slauthEgress: false,
		slauthAudience: '',
		extraHeaders: undefined,
	});

	const cryptorBaseUrl = process.env['TCS_SIDECAR_HTTP_URL'];
	const cryptorAxiosInstance = await axiosClient(
		{ name: 'tcs-sidecar', ...opts },
		{ baseURL: cryptorBaseUrl, ...config },
	);
	const cryptorHttpClient = new HttpClient({
		axiosInstance: cryptorAxiosInstance,
		slauthEgress: false,
		slauthAudience: '',
		extraHeaders: {
			'X-Cryptor-Client': tcsSidecarApiOptions?.cryptorClientId ?? DEFAULT_CRYPTOR_CLIENT_ID,
			'Content-Type': 'application/json; charset=utf-8',
		},
	});

	return new TcsSidecarApi(tcsHttpClient, cryptorHttpClient);
}
